import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function useNavCounts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Realtime invalidation
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel("nav-counts")
      .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, () => {
        queryClient.invalidateQueries({ queryKey: ["nav-counts", user.id] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "shipments" }, () => {
        queryClient.invalidateQueries({ queryKey: ["nav-counts", user.id] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => {
        queryClient.invalidateQueries({ queryKey: ["nav-counts", user.id] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "support_tickets" }, () => {
        queryClient.invalidateQueries({ queryKey: ["nav-counts", user.id] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "helpdesk_settings" }, () => {
        queryClient.invalidateQueries({ queryKey: ["nav-counts", user.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  return useQuery({
    queryKey: ["nav-counts", user?.id],
    queryFn: async () => {
      const userId = user!.id;

      const [settingsRes, ordersRes, shipmentsRes, ticketsRes, invoicesRes, planningRes, inboxRes] = await Promise.all([
        (supabase as any)
          .from("helpdesk_settings")
          .select("telephony_notifications_enabled")
          .eq("id", true)
          .maybeSingle(),
        // Orders with status 'submitted' (own only)
        supabase
          .from("orders")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("status", "submitted"),
        // Shipments with status 'submitted' (own only)
        supabase
          .from("shipments")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("status", "submitted"),
        // Open support tickets (excluding calls when telephony notifications are disabled)
        (async () => {
          const telephonyEnabled = (await (supabase as any)
            .from("helpdesk_settings")
            .select("telephony_notifications_enabled")
            .eq("id", true)
            .maybeSingle())?.data?.telephony_notifications_enabled !== false;
          let q = supabase
            .from("support_tickets")
            .select("*", { count: "exact", head: true })
            .eq("status", "open");
          if (!telephonyEnabled) {
            q = q.neq("source", "call");
          }
          return q;
        })(),
        // Open invoices (own orders, not draft, not paid)
        supabase
          .from("orders")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId)
          .neq("status", "draft")
          .neq("invoice_status", "paid"),
        // Recurring/subscription orders (own only)
        supabase
          .from("orders")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("is_recurring", true)
          .not("status", "in", '("completed","cancelled")'),
        // Unread notifications (inbox)
        supabase
          .from("notifications")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("read", false),
      ]);

      return {
        orders: ordersRes.count || 0,
        shipments: shipmentsRes.count || 0,
        invoices: invoicesRes.count || 0,
        helpdesk: ticketsRes.count || 0,
        planning: planningRes.count || 0,
        inbox: inboxRes.count || 0,
      };
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });
}
